import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, CalendarDays, Search, Loader2 } from "lucide-react";

import api, { formatApiError } from "../lib/api";
import EUDatePicker from "../components/EUDatePicker";
import SearchField from "../components/public-book/SearchField";
import VehicleCard from "../components/public-book/VehicleCard";
import BookingModal from "../components/public-book/BookingModal";
import InquiryModal from "../components/public-book/InquiryModal";
import {
  isoToday,
  isoInDays,
  formatEU,
  timeInBlackout,
  CATEGORY_ORDER,
} from "../components/public-book/formatters";

/**
 * Public Booking Page (/book) — v3.9.4.
 *
 * Distraction-free customer surface. No admin sidebar, no auth chrome.
 * Hero search bar → vehicle results grouped by category → guest checkout
 * modal → confirmed booking + reference ID.
 *
 * Extracted sub-components live in `/components/public-book/*`.
 */
export default function PublicBook() {
  const [sp] = useSearchParams();
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({
    pickup_loc:   sp.get("pickup_loc")   || "",
    return_loc:   sp.get("return_loc")   || "",
    pickup_date:  sp.get("pickup_date")  || isoToday(),
    pickup_time:  sp.get("pickup_time")  || "10:00",
    return_date:  sp.get("return_date")  || isoInDays(3),
    return_time:  sp.get("return_time")  || "10:00",
  });
  const [vehicles, setVehicles] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [inquiryVehicle, setInquiryVehicle] = useState(null);
  const [settings, setSettings] = useState(null);
  const [extras, setExtras] = useState([]);

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => setSettings(null));
    api.get("/extras").then((r) => setExtras(Array.isArray(r.data) ? r.data : [])).catch(() => setExtras([]));
  }, []);

  useEffect(() => {
    api.get("/settings/locations")
      .then((r) => {
        const locs = Array.isArray(r.data) ? r.data : [];
        setLocations(locs);
        setForm((f) => ({
          ...f,
          pickup_loc: f.pickup_loc || locs[0]?.name || "",
          return_loc: f.return_loc || locs[0]?.name || "",
        }));
      })
      .catch(() => setLocations([]));
  }, []);

  const days = useMemo(() => {
    if (!form.pickup_date || !form.return_date) return 0;
    const a = new Date(form.pickup_date), b = new Date(form.return_date);
    return Math.max(1, Math.round((b - a) / 86_400_000));
  }, [form.pickup_date, form.return_date]);

  // ── Calendar constraints (v3.8.3) ────────────────────────────────
  // v4.2.2 — brand + hero copy are dynamic (Admin → Business Settings).
  const brandName = settings?.brand_name || "Brainart Rent";
  const regionLabel = settings?.operating_region || brandName;
  const heroTitle = settings?.public_hero_title || "Book your premium ride in under a minute.";
  const leadDays = Math.max(0, Number(settings?.release_date_days ?? 0));
  const minRentalDays = Math.max(1, Number(settings?.min_rental_days ?? 1));
  const pickupMinIso = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + leadDays);
    return d.toISOString().slice(0, 10);
  }, [leadDays]);
  const returnMinIso = useMemo(() => {
    if (!form.pickup_date) return pickupMinIso;
    const d = new Date(form.pickup_date);
    d.setDate(d.getDate() + minRentalDays);
    return d.toISOString().slice(0, 10);
  }, [form.pickup_date, minRentalDays, pickupMinIso]);

  // ── Location blackout (No-Service window) validation (v3.8.6) ────
  const pickupLocObj = useMemo(
    () => locations.find((l) => l.name === form.pickup_loc) || null,
    [locations, form.pickup_loc],
  );
  const returnLocObj = useMemo(
    () => locations.find((l) => l.name === form.return_loc) || null,
    [locations, form.return_loc],
  );
  const blackoutErrors = useMemo(() => {
    const errs = [];
    if (pickupLocObj?.blackout_enabled && timeInBlackout(form.pickup_time, pickupLocObj.blackout_from, pickupLocObj.blackout_to)) {
      errs.push({
        side:  "pickup",
        name:  pickupLocObj.name,
        from:  pickupLocObj.blackout_from,
        to:    pickupLocObj.blackout_to,
      });
    }
    if (returnLocObj?.blackout_enabled && timeInBlackout(form.return_time, returnLocObj.blackout_from, returnLocObj.blackout_to)) {
      errs.push({
        side:  "return",
        name:  returnLocObj.name,
        from:  returnLocObj.blackout_from,
        to:    returnLocObj.blackout_to,
      });
    }
    return errs;
  }, [pickupLocObj, returnLocObj, form.pickup_time, form.return_time]);
  const hasBlackout = blackoutErrors.length > 0;

  const search = async () => {
    if (!form.pickup_loc || !form.return_loc || !form.pickup_date || !form.return_date) {
      toast.warning("Complete pickup & return locations and dates first.");
      return;
    }
    if (hasBlackout) {
      toast.error(blackoutErrors[0] && `Location closed between ${blackoutErrors[0].from} and ${blackoutErrors[0].to}`);
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get("/public/vehicles", {
        params: {
          pickup_date:     form.pickup_date,
          return_date:     form.return_date,
          pickup_time:     form.pickup_time,
          return_time:     form.return_time,
          pickup_location: form.pickup_loc,
        },
      });
      setVehicles(data.vehicles || []);
      const available = (data.vehicles || []).filter((v) => v.available_for_dates !== false).length;
      toast.success(`${available} vehicles available`, { description: `${formatEU(form.pickup_date)} → ${formatEU(form.return_date)}` });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSearching(false);
    }
  };

  // Auto-search when URL params provide a full window on first mount.
  useEffect(() => {
    if (form.pickup_loc && form.return_loc && form.pickup_date && form.return_date) {
      const t = setTimeout(search, 250);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line
  }, [locations.length]);

  const grouped = useMemo(() => {
    const g = new Map();
    for (const v of vehicles) {
      const cat = (v.category || "other").toLowerCase();
      if (!g.has(cat)) g.set(cat, []);
      g.get(cat).push(v);
    }
    return Array.from(g.entries()).sort(([a], [b]) => {
      const ia = CATEGORY_ORDER.indexOf(a);
      const ib = CATEGORY_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  }, [vehicles]);

  return (
    <div className="min-h-screen bg-[#F7F7F5] text-neutral-900" data-testid="public-book-page">
      <header className="border-b border-black/8 bg-white/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <a href="/book" className="font-display text-xl tracking-tight">
            {brandName}
          </a>
          <nav className="text-xs text-neutral-500 flex items-center gap-5">
            <a href="#fleet" className="hover:text-black transition-colors">Our fleet</a>
            <a href="/manage-booking" className="hover:text-black transition-colors" data-testid="pb-nav-manage-booking">Manage Booking</a>
            <a href="mailto:hello@brainart.gr" className="hover:text-black transition-colors">Contact</a>
          </nav>
        </div>
      </header>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-10 pb-6">
        <p className="text-[10px] tracking-[0.35em] uppercase text-neutral-500">Rent-a-car · {regionLabel}</p>
        <h1 className="font-display text-4xl md:text-6xl leading-[1.05] mt-3 max-w-3xl">
          {heroTitle}
        </h1>
        <p className="text-neutral-500 mt-3 max-w-xl text-sm">
          Airport &amp; Port pickups · Instant confirmation · No hidden fees.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10" data-testid="public-search-bar">
        <div className="flex justify-end mb-2">
          <a
            href="/manage-booking"
            className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 hover:text-black transition-colors underline underline-offset-4"
            data-testid="pb-search-manage-booking"
          >
            Manage Booking →
          </a>
        </div>
        <div className="bg-white border border-black/10 rounded-lg shadow-sm p-4 lg:p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.1fr_1.1fr_1.1fr_1.1fr_auto] gap-3 lg:gap-4">
          <SearchField label="Pickup location" icon={<MapPin size={14} />}>
            <select
              value={form.pickup_loc}
              onChange={(e) => setForm((f) => ({ ...f, pickup_loc: e.target.value, return_loc: f.return_loc || e.target.value }))}
              className="pb-input"
              data-testid="pb-pickup-loc"
            >
              {locations.map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
            </select>
          </SearchField>

          <SearchField label="Pickup date &amp; time" icon={<CalendarDays size={14} />}>
            <div className="flex gap-2">
              <div className="flex-1" data-testid="pb-pickup-date-wrap">
                <EUDatePicker
                  value={form.pickup_date}
                  onChange={(iso) => setForm((f) => {
                    // v4.2.7 — always keep return ≥ pickup + min rental days.
                    const d = new Date(iso);
                    d.setDate(d.getDate() + minRentalDays);
                    const minReturn = d.toISOString().slice(0, 10);
                    return {
                      ...f,
                      pickup_date: iso,
                      return_date:
                        f.return_date && f.return_date >= minReturn ? f.return_date : minReturn,
                    };
                  })}
                  minIso={pickupMinIso}
                  testId="pb-pickup-date"
                />
              </div>
              <input
                type="time"
                value={form.pickup_time}
                onChange={(e) => setForm((f) => ({ ...f, pickup_time: e.target.value }))}
                className="pb-input w-[95px]"
                data-testid="pb-pickup-time"
              />
            </div>
          </SearchField>

          <SearchField label="Return location" icon={<MapPin size={14} />}>
            <select
              value={form.return_loc}
              onChange={(e) => setForm((f) => ({ ...f, return_loc: e.target.value }))}
              className="pb-input"
              data-testid="pb-return-loc"
            >
              {locations.map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
            </select>
          </SearchField>

          <SearchField label="Return date &amp; time" icon={<CalendarDays size={14} />}>
            <div className="flex gap-2">
              <div className="flex-1" data-testid="pb-return-date-wrap">
                <EUDatePicker
                  value={form.return_date}
                  onChange={(iso) => setForm((f) => ({ ...f, return_date: iso }))}
                  minIso={returnMinIso}
                  testId="pb-return-date"
                />
              </div>
              <input
                type="time"
                value={form.return_time}
                onChange={(e) => setForm((f) => ({ ...f, return_time: e.target.value }))}
                className="pb-input w-[95px]"
                data-testid="pb-return-time"
              />
            </div>
          </SearchField>

          <button
            type="button"
            onClick={search}
            disabled={searching || hasBlackout}
            className="bg-black text-white text-sm font-medium px-6 py-3 rounded inline-flex items-center justify-center gap-2 hover:bg-neutral-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="pb-search-btn"
          >
            {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            {searching ? "Searching…" : "Search Cars"}
          </button>
        </div>
        {hasBlackout && (
          <div className="mt-3 rounded border border-rose-300 bg-rose-50 px-4 py-2.5 text-xs text-rose-800" data-testid="pb-blackout-error">
            {blackoutErrors.map((e, i) => (
              <p key={i}>
                <strong>{e.name}</strong> — Location is closed for pickups
                and returns between <strong>{e.from}</strong> and <strong>{e.to}</strong>.
              </p>
            ))}
          </div>
        )}
        {days > 0 && !hasBlackout && (
          <p className="mt-2 text-base font-semibold text-neutral-800 tracking-tight" data-testid="pb-window-summary">
            Window: <span className="tabular-nums">{formatEU(form.pickup_date)}</span> →{" "}
            <span className="tabular-nums">{formatEU(form.return_date)}</span>{" "}
            · {days} day{days === 1 ? "" : "s"}
          </p>
        )}
      </section>

      <main id="fleet" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        {searching && vehicles.length === 0 && (
          <div className="py-24 text-center text-neutral-500">
            <Loader2 size={20} className="mx-auto animate-spin" />
            <p className="mt-4 text-sm">Loading fleet…</p>
          </div>
        )}
        {!searching && vehicles.length === 0 && (
          <div className="py-24 text-center text-neutral-500">
            <p className="text-sm">Choose your dates and hit “Search Cars” to see what's available.</p>
          </div>
        )}
        {grouped.map(([cat, list]) => (
          <section key={cat} className="mb-14" data-testid={`pb-cat-${cat}`}>
            <div className="flex items-baseline justify-between mb-4 border-b border-black/8 pb-2">
              <h2 className="font-display text-2xl capitalize">{cat}</h2>
              <span className="text-xs text-neutral-500">{list.length} vehicle{list.length === 1 ? "" : "s"}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {list.map((v) => (
                <VehicleCard
                  key={v.id}
                  vehicle={v}
                  days={days}
                  onBook={() => (v.status === "upon_request" ? setInquiryVehicle(v) : setSelected(v))}
                />
              ))}
            </div>
          </section>
        ))}
      </main>

      {selected && (
        <BookingModal
          vehicle={selected}
          form={form}
          days={days}
          settings={settings}
          extras={extras}
          onClose={() => setSelected(null)}
        />
      )}

      {inquiryVehicle && (
        <InquiryModal
          vehicle={inquiryVehicle}
          form={form}
          onClose={() => setInquiryVehicle(null)}
        />
      )}

      <footer className="border-t border-black/8 mt-10 py-8 text-center text-xs text-neutral-500 space-y-1">
        <p>{brandName} · No account required · Booking confirmations sent to your email.</p>
        <p data-testid="pb-footer-copyright">
          © {new Date().getFullYear()} {brandName}. All rights reserved.
        </p>
      </footer>

      <style>{`
        .pb-input {
          width: 100%;
          height: 40px;                /* v4.0.1 — align with input-group height */
          border: 1px solid rgba(0,0,0,0.15);
          background: #FFF;
          font-size: 14px;
          padding: 8px 10px;
          border-radius: 4px;
          color: #111;
          outline: none;
          transition: border-color .18s ease;
        }
        .pb-input:focus { border-color: #111; }
        textarea.pb-input, .pb-input[rows] { height: auto; min-height: 60px; }
      `}</style>
    </div>
  );
}
